// third-party
import { createSlice } from '@reduxjs/toolkit';

// project imports
import { dispatch } from '../index';
import * as twilioAPI from 'api/twilio';
import * as alertSnackbar from 'utils/snackbar-wrapper';

const initialState = {
    error: false,
    loaded: false,
    // Which patient `data` describes. One shared bucket serves every patient,
    // so consumers check this stamp instead of trusting whatever response
    // landed last.
    patientId: null,
    data: {
        enabled: false,
        callerId: null,
        defaultCallerId: null,
        callerIds: []
    }
};

// What a consumer reads when the stored config belongs to another patient.
export const EMPTY_OUTBOUND_CALLING_CONFIG = initialState.data;

const slice = createSlice({
    name: 'twilioOutboundCallingConfig',
    initialState,
    reducers: {
        setConfigError(state, action) {
            state.error = true;
            state.loaded = true;
            state.patientId = action.payload;
            // Don't leave the previous patient's numbers behind a failed fetch.
            state.data = initialState.data;
        },
        setConfig(state, action) {
            state.error = false;
            state.loaded = true;
            state.patientId = action.payload.patientId;
            state.data = action.payload.data;
        },
        resetConfigState() {
            return initialState;
        }
    }
});

// Reducer
export default slice.reducer;

// ----------------------------------------------------------------------

export function resetTwilioOutboundCallingConfigState() {
    dispatch(slice.actions.resetConfigState());
}

export function getTwilioOutboundCallingConfig(clinicId, patientId) {
    return async () => {
        const response = await twilioAPI.getOutboundCallingConfig(clinicId, patientId);
        if (response?.data && typeof response.data.enabled === 'boolean') {
            dispatch(slice.actions.setConfig({ patientId, data: response.data }));
        } else {
            // `response.message` only exists on the API's error return; a malformed 200 body
            // (unexpected shape) is the raw axios response with no `.message`, so guard it.
            const reason = response?.message ?? 'unexpected response';
            dispatch(alertSnackbar.error(`Failed to fetch outbound calling config: ${reason}`));
            dispatch(slice.actions.setConfigError(patientId));
        }
    };
}
